import React, { useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import useStore from "../../../context/useStore";
import sidenavItems from "../../../sidenavItems";
import { libelleDuRole } from "../../common/layout/MenuLateral/MenuLateral";
import useAllRecipients from "../AllRecipients/useAllRecipients";
import useIndicateursDuJour from "./useIndicateursDuJour";
import { dateDuJour, estVisible, filtresDepuisTerme, salutation, sectionsDuTableauDeBord } from "./raccourcis";
import "./dashboard.scss";

// ============================================================
// Tableau de bord : salutation et date, recherche rapide d'un allocataire,
// indicateurs du jour (EP, invitations, anomalies, réorientations) puis les
// raccourcis regroupés comme le menu — mêmes entrées, même ordre, même
// filtrage par rôle (sectionsDuTableauDeBord). Les entrées gardent leur
// data-priv pour que RoleStyle les masque aussi en CSS.
// ============================================================

const Indicateur = ({ indicateur }) => {
  const { statut, valeur, detail, ton, libelle, icone, path } = indicateur;
  const classe = `indicateur indicateur--${statut === "ok" ? ton || "neutral" : statut}`;
  return (
    <li>
      <Link to={path} className={classe} aria-busy={statut === "chargement" || undefined}>
        <span className="indicateur__icone" aria-hidden="true">
          <i className={`fa fa-${icone}`} />
        </span>
        <span className="indicateur__valeur">
          {statut === "chargement" ? (
            <span className="skeleton-bar indicateur__squelette" />
          ) : statut === "erreur" ? (
            "—"
          ) : (
            valeur
          )}
        </span>
        <span className="indicateur__libelle">{libelle}</span>
        <span className="indicateur__detail">
          {statut === "chargement" ? "Chargement…" : statut === "erreur" ? "Indisponible pour le moment" : detail}
        </span>
      </Link>
    </li>
  );
};

const Dashboard = () => {
  const [state] = useStore();
  const user = state.auth.user;
  const constantes = Array.isArray(state.constants.items) ? {} : state.constants.items;
  const history = useHistory();
  const sections = useMemo(() => sectionsDuTableauDeBord(sidenavItems, user || {}), [user]);
  const indicateurs = useIndicateursDuJour(sections);
  const rechercheVisible = estVisible(sections, "/all-recipients");
  const { onUpdateFilters, searchAllRecipients } = useAllRecipients();
  const [terme, setTerme] = useState("");

  const rechercher = (e) => {
    e.preventDefault();
    const filtres = filtresDepuisTerme(terme);
    if (!filtres) return;
    onUpdateFilters(filtres);
    searchAllRecipients(filtres);
    history.push("/all-recipients");
  };

  return (
    <div className="container-fluid px-5 my-5 tableau-de-bord">
      <header className="tdb-entete">
        <div className="tdb-entete__texte">
          <p className="tdb-entete__eyebrow">{dateDuJour()}</p>
          <h1 className="tdb-entete__titre">{salutation(user || {})}</h1>
          <p className="tdb-entete__sous-titre">{libelleDuRole(user || {}, constantes)}</p>
        </div>
        {rechercheVisible && (
          <form className="tdb-recherche list-card" role="search" onSubmit={rechercher}>
            <label htmlFor="tdb-recherche" className="sr-only">
              Rechercher un allocataire par nom, NIR ou matricule
            </label>
            <i className="fa fa-search" aria-hidden="true" />
            <input
              id="tdb-recherche"
              type="search"
              className="form-control"
              placeholder="Nom, NIR ou matricule d'un allocataire…"
              value={terme}
              onChange={(e) => setTerme(e.target.value)}
              autoComplete="off"
            />
            <button type="submit" className="btn btn-primary" disabled={!terme.trim()}>
              Rechercher
            </button>
          </form>
        )}
      </header>

      {indicateurs.length > 0 && (
        <section className="tdb-section" aria-labelledby="tdb-aujourdhui">
          <h2 id="tdb-aujourdhui" className="tdb-section__titre">
            <i className="fa fa-sun" aria-hidden="true" />
            Aujourd'hui
          </h2>
          <ul className="tdb-indicateurs">
            {indicateurs.map((ind) => (
              <Indicateur key={ind.id} indicateur={ind} />
            ))}
          </ul>
        </section>
      )}

      {sections.map((section) => (
        <section key={section.id} className="tdb-section" aria-labelledby={`tdb-${section.id}`}>
          <h2 id={`tdb-${section.id}`} className="tdb-section__titre">
            <i className={`fa fa-${section.icon}`} aria-hidden="true" />
            {section.name}
          </h2>
          <ul className="tdb-tuiles">
            {section.entrees.map((entree) => (
              <li key={entree.path} data-priv={entree.dataPriv || undefined}>
                <Link to={entree.path} className="tuile">
                  <span className="tuile__icone" aria-hidden="true">
                    <i className={`fa fa-${entree.icon}`} />
                  </span>
                  <span className="tuile__texte">
                    <span className="tuile__nom">{entree.name}</span>
                    {entree.description && <span className="tuile__desc">{entree.description}</span>}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default Dashboard;
